enum AuthActionType {
  Authenticate = 'authenticate',
  Reauthenticate = 'reauthenticate',
  RemoveAuthentication = 'removeAuthentication',
  ExchangeToken = 'exchangeToken',
  DismissBanner = 'dismissBanner',
}

interface AuthState {
  /**
   * Whether the user is authenticated and has a valid JWT token stored in cookies.
   */
  isAuthenticated: boolean;
  /**
   * Whether the user is a Vizit user.
   */
  isVizitUser?: boolean;
  /**
   * Whether the user is displaying the banner.
   */
  isDisplayingBanner?: boolean;
  /**
   * The ID of the organization the user is authenticated for.
   */
  organizationId?: string | null;
  /**
   * The name of the organization the user is authenticated for.
   */
  organizationName?: string;
  /**
   * The ID of the default portfolio the the organization the user is authenticated for.
   */
  defaultPortfolioId?: string;
  /**
   * The default retailer for the organization the user is authenticated for.
   */
  defaultRetailer?: string;
  /**
   * The ID of the current signed in user.
   */
  userId?: string | null;
  /**
   * The email address of the current signed in user.
   */
  userEmail?: string;
}

function authStateReducer(
  currentState: AuthState,
  action: { type: AuthActionType; payload?: Partial<AuthState> },
): AuthState {
  const { organizationId, organizationName, defaultPortfolioId, defaultRetailer, userId, userEmail } =
    action.payload ?? {};
  const isVizitUser = currentState.userEmail?.includes('@vizit.com') || userEmail?.includes('@vizit.com');

  switch (action.type) {
    case AuthActionType.Authenticate:
    case AuthActionType.Reauthenticate:
      return {
        ...currentState,
        isVizitUser,
        isAuthenticated: true,
        organizationId,
        organizationName,
        defaultPortfolioId,
        defaultRetailer,
        userId,
        userEmail,
      };
    case AuthActionType.RemoveAuthentication:
      return {
        isVizitUser: false,
        isAuthenticated: false,
      };
    case AuthActionType.ExchangeToken:
      return {
        ...currentState,
        isDisplayingBanner: !!isVizitUser, // Always show the banner when user changes organizations, but only for Vizit users.
        isVizitUser,
        organizationId,
        organizationName,
        defaultPortfolioId,
        defaultRetailer,
      };
    case AuthActionType.DismissBanner:
      return {
        ...currentState,
        isDisplayingBanner: false,
      };
    default:
      return currentState;
  }
}

export { AuthActionType, authStateReducer };
export type { AuthState };
